import React from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getRiskToleranceHelpModalOpen, toggleRiskToleranceHelpModal } from '../../admin/dataset-builder/store';
import { SingleIndicator } from '../analytics/SingleIndicator';
import { SmileyNeutral } from '../icons';
import { ConfirmationModal } from '../modal/ConfirmationModal';
import { ModalHeader } from '../modal/ModalHeader';
import styles from './RiskTolerance.module.scss';
import { riskAssociatedColors } from '../../admin/risk-tolerance/utils';
import { SmileyIndicator, RiskAssociated } from '../../admin/risk-tolerance/store';
import { Scrollable } from '../scrollable/Scrollable';

const { primary, white, fixedGrey, fixedPrimary, lightGrey } = styles;

const generalExplanation = [
    'Certain document fields have been designated as risk fields by our system. These are fields where the answer may impact the overall risk of the agreement, such as "Governing Law".',
    'The risk tolerance for one of these fields is split into 5 categories: Of Serious Concern, Of Concern, Acceptable, Desirable, Highly Desirable.',
    'These fields can then help us identify the overall risk of an agreement, this can be seen within the document analytics.'
];

const riskLevels = [
    { indicator: SmileyIndicator.POSITIVE, fill: riskAssociatedColors[RiskAssociated.HIGHLY_DESIRABLE], color: fixedPrimary, label: 'The answer for the field is Highly Desirable' },
    { indicator: SmileyIndicator.POSITIVE, fill: riskAssociatedColors[RiskAssociated.DESIRABLE], color: fixedPrimary, label: 'The answer for the field is Desirable' },
    { indicator: SmileyIndicator.NEUTRAL, fill: riskAssociatedColors[RiskAssociated.ACCEPTABLE], color: fixedPrimary, label: 'The answer for the field is Acceptable' },
    { indicator: SmileyIndicator.NEGATIVE, fill: riskAssociatedColors[RiskAssociated.OF_CONCERN], color: fixedPrimary, label: 'The answer for the field is Of Concern' },
    { indicator: SmileyIndicator.NEGATIVE, fill: riskAssociatedColors[RiskAssociated.OF_SERIOUS_CONCERN], color: fixedPrimary, label: 'The answer for the field is Of Serious Concern' },
    { indicator: SmileyIndicator.NONE, color: fixedGrey, fill: lightGrey, label: 'Either no value for the fields has been provided, or it does not match any of the risk tolerance configuration criteria' }
];

export const RiskToleranceHelpModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector(getRiskToleranceHelpModalOpen);
    const closeModal = () => dispatch(toggleRiskToleranceHelpModal());

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            closeLabel='Close'
            showConfirm={false}
        >
            <div className={styles.riskToleranceHelpWrapper}>
                <ModalHeader icon={SmileyNeutral} label='Risk Tolerance Help' iconColor={primary} iconFill={white} />
                <div className={styles.helpContent}>
                    <div className={styles.generalHelpContent}>
                        {generalExplanation.map((help, index) => (
                            <div key={index} className={styles.generalHelp}>{help}</div>
                        ))}
                    </div>
                    <div className={styles.riskLevelContentWrapper}>
                        <div className={styles.riskLevelHelpTitle}>Any risk fields will be shown with one of the following indicators</div>
                        <div className={styles.riskLevelContent}>
                            <Scrollable>
                                {riskLevels.map(({ indicator, color, fill, label }, index) => (
                                    <div className={styles.riskLevelWrapper} key={index}>
                                        <SingleIndicator indicator={indicator} color={color} fill={fill} fontSize={50} />
                                        <div>{label}</div>
                                    </div>
                                ))}
                            </Scrollable>
                        </div>
                    </div>
                </div>
            </div>
        </ ConfirmationModal>
    );
};
